/**
 * ストリートビュー机上調査の一括解析パネル (docs/25 §7-4、2026-07-15 に線描画ソースを追加)。
 *
 * 候補点の生成元は2種類 (`sourceMode`):
 *   - 'layer': 道路線 GIS レイヤーを選び → `computeStreetViewCandidatePoints` で交差点＋
 *     補間点の候補点を生成。
 *   - 'line': 地図 (`StreetSurveyResultsMap`) 上でクリックして描いた線 (`initialLine` 経由で
 *     このパネルに渡される) から、単純な等間隔の補間点を生成。
 * 補間間隔 (`intervalM`) はどちらの生成元でも共通の入力欄で5m単位で指定可能 (既定100m、
 * 2026-07-15に「約100m」決め打ちを撤廃)。
 * どちらの生成元でも Street View 存在フィルタ済みの候補点をプレビュー → 件数上限を確認して
 * `analyzeStreetViewBatch` でジョブを起動 → ジョブ doc (`streetViewBatchJobs/{jobId}`) を
 * 購読して進捗を表示する、という後半のフローは完全に共通。master 限定
 * (`DistrictForceReanalyzePanel.tsx` の方針を踏襲)。
 */
import { Button, Card, Modal, Tabs, TextField, useToast } from '@citizen-walk/shared-ui';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useRef, useState } from 'react';
import { functions } from '../../lib/firebase';
import type { LatLng } from '../bulk-photo-import';
import { importGisFile, useGisLayers } from '../gis-admin';
import { useAdminAuth } from '../auth';
import { useStreetSurveyBatchJob } from './useStreetSurveyBatchJob';
import { computeAxisCounts, computeEvaluationSummary } from './computeAreaAxisCounts';
import { STREET_SURVEY_BATCH_MAX_POINTS, type EvaluationAxisDef } from '@citizen-walk/shared-types';
import { saveRouteReportSnapshot } from './routeReportSnapshotActions';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';

const GIS_ACCEPT = '.gpkg,.kml,.kmz,.geojson,.json';

interface CandidatePoint {
  lat: number;
  lng: number;
  isIntersection: boolean;
  headingDeg?: number;
}

type ComputeCandidatePointsResult =
  | {
      ok: true;
      candidates: CandidatePoint[];
      totalBeforeStreetViewFilter: number;
      truncated: boolean;
      /** サーバー側で実際に使用された値 (デバッグ表示用、2026-07-15追加)。 */
      intervalM: number;
      minDistanceM: number;
      /** 手描き線 (`sourceMode==='line'`) の全長 (m)。路線調査の距離正規化・偏差値比較
       *  (docs/25 §10-6) に使う。layer 由来では未対応のため undefined。 */
      totalLengthM?: number;
    }
  | { ok: false; error: string };

type AnalyzeStreetViewBatchResult = { ok: true; jobId: string } | { ok: false; error: string };

export interface StreetSurveyBatchPanelProps {
  districtId: string;
  /** 集計している調査 (docs/98 R-97、路線の保存に使う)。 */
  surveyKey?: string;
  /** 1 地点の AI 呼出回数 (新方式 5 / 従来 4)。費用の目安の表示に使う。 */
  callsPerPoint?: number;
  isMaster: boolean;
  open: boolean;
  /** 地図で描いた線 (`StreetSurveyResultsMap` の onLineDrawn 経由)。開いた時点で設定されて
   *  いれば「地図で線を描く」タブを自動で選択する (docs/25 §7-4 拡張、2026-07-15)。 */
  initialLine?: LatLng[] | null;
  /** 実行中/直近のジョブ id。`StreetSurveyPage.tsx` が保持する (2026-07-16: パネルを閉じても
   *  進捗追跡が失われないよう、このパネルではなく親に状態を持たせた)。 */
  jobId: string | null;
  /** 一括解析実行時、新しく発行された jobId を親に伝える。 */
  onJobStarted: (jobId: string) => void;
  onClose: () => void;
  /** 路線調査の保存 (docs/25 §10-6、「路線として保存」) 用。ジョブ完了後、対象 post から
   *  役割ごと検出件数を集計するために必要 (`StreetSurveyPage.tsx` から配線)。 */
  posts: StreetSurveyPost[];
  primaryAxis: PrimaryAxisResolution;
  /** docs/27 §9: 評価軸と集計に使った調査項目の key (路線保存時にスナップショットへ記録)。 */
  evaluation?: EvaluationAxisDef | null;
  axisKey?: string | null;
}

/** 1 回に実行できる件数の上限 (analyzeStreetViewBatch と共有)。 */
const MAX_EXECUTE = STREET_SURVEY_BATCH_MAX_POINTS;
/** 候補を出したときの実行件数の初期値 (費用がかかるため、上限より小さくしておく)。 */
const DEFAULT_EXECUTE = 50;

type SourceMode = 'layer' | 'line';

export function StreetSurveyBatchPanel({
  districtId,
  isMaster,
  open,
  initialLine,
  jobId,
  onJobStarted,
  onClose,
  surveyKey,
  callsPerPoint = 5,
  posts,
  primaryAxis,
  evaluation = null,
  axisKey = null,
}: StreetSurveyBatchPanelProps) {
  const { toast } = useToast();
  const { state: authState } = useAdminAuth();
  const layersState = useGisLayers(open ? districtId : null);
  const lineLayers = layersState.layers.filter(
    (l) => l.geometryType === 'line' || l.geometryType === 'mixed',
  );

  const [layerId, setLayerId] = useState('');
  const [sourceMode, setSourceMode] = useState<SourceMode>('layer');
  const [lineCoords, setLineCoords] = useState<LatLng[] | null>(null);
  /** 候補点の補間間隔 (m)。5m単位、既定100m (2026-07-15: 決め打ちを撤廃しユーザー指定可能に)。 */
  const [intervalM, setIntervalM] = useState(100);
  const [computing, setComputing] = useState(false);
  const [candidateResult, setCandidateResult] = useState<ComputeCandidatePointsResult | null>(null);
  const [executeCount, setExecuteCount] = useState(DEFAULT_EXECUTE);
  const [starting, setStarting] = useState(false);
  const [savingRouteName, setSavingRouteName] = useState<string | null>(null);
  const [savingRoute, setSavingRoute] = useState(false);
  const job = useStreetSurveyBatchJob(districtId, jobId, (error) =>
    toast('error', `進捗の購読に失敗しました: ${error.message}`),
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // このパネルは open=false でもアンマウントされない (下の `if (!open) return null` は
  // 全 hooks 評価後) ため、initialLine の反映は effect で行う (2026-07-15)。
  useEffect(() => {
    if (open && initialLine && initialLine.length >= 2) {
      setSourceMode('line');
      setLineCoords(initialLine);
      setCandidateResult(null);
    }
  }, [open, initialLine]);

  // jobId/job (進捗) は親 (`StreetSurveyPage.tsx`) が保持するため、ここではリセットしない
  // (2026-07-16: 以前はここで jobId を破棄していたため、パネルを閉じると進捗を再表示する
  // 手段が失われるバグがあった)。候補点選択フェーズの入力状態のみリセットする。
  function handleClose() {
    // ジョブが実行中/完了している間 (jobId あり) は candidateResult (totalLengthM を含む)・
    // lineCoords 等の入力フェーズ状態をリセットしない。ここで消してしまうと、最小化 (badge化)
    // → 再度開く、という操作の後に「路線として保存」が totalLengthM 欠落で出せなくなるバグが
    // あった (2026-07-21、実運用で発覚)。まだジョブが無い (入力フェーズのまま閉じた) 場合のみ
    // 従来通り全リセットする。
    if (!jobId) {
      setLayerId('');
      setSourceMode('layer');
      setLineCoords(null);
      setIntervalM(100);
      setCandidateResult(null);
    }
    setUploadFile(null);
    setSavingRouteName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  }

  async function handleUpload() {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const res = await importGisFile(districtId, uploadFile);
      const lineLayer = res.layers.find(
        (l) => l.geometryType === 'line' || l.geometryType === 'mixed',
      );
      toast('success', `${res.layers.length} レイヤーを取り込みました。`);
      if (lineLayer) {
        setLayerId(lineLayer.id);
        setCandidateResult(null);
      }
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      toast('error', `道路データのアップロードに失敗しました: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  async function handleCompute() {
    if (sourceMode === 'layer' && !layerId) return;
    if (sourceMode === 'line' && (!lineCoords || lineCoords.length < 2)) return;
    setComputing(true);
    setCandidateResult(null);
    try {
      const source =
        sourceMode === 'layer'
          ? ({ type: 'layer', layerId } as const)
          : ({
              type: 'line',
              coordinates: (lineCoords ?? []).map((c) => ({ lat: c.latitude, lng: c.longitude })),
            } as const);
      const callable = httpsCallable<
        { districtId: string; source: typeof source; intervalM: number },
        ComputeCandidatePointsResult
      >(functions, 'computeStreetViewCandidatePoints', { timeout: 300000 });
      const res = await callable({ districtId, source, intervalM });
      setCandidateResult(res.data);
      if (res.data.ok) {
        setExecuteCount(Math.min(DEFAULT_EXECUTE, res.data.candidates.length));
      } else {
        toast('error', `候補点の計算に失敗しました: ${res.data.error}`);
      }
    } catch (e) {
      toast('error', `候補点の計算に失敗しました: ${(e as Error).message}`);
    } finally {
      setComputing(false);
    }
  }

  async function handleExecute() {
    if (!candidateResult?.ok) return;
    const points = candidateResult.candidates.slice(0, executeCount).map((c) => ({
      lat: c.lat,
      lng: c.lng,
      ...(c.headingDeg !== undefined ? { headingDeg: c.headingDeg } : {}),
    }));
    setStarting(true);
    try {
      const callable = httpsCallable<
        {
          districtId: string;
          points: { lat: number; lng: number; headingDeg?: number }[];
          surveyKey?: string;
        },
        AnalyzeStreetViewBatchResult
      >(functions, 'analyzeStreetViewBatch');
      // docs/98 R-97: 追加の調査を選んでいれば、その調査で解析する (新しい地点も作る)。
      const res = await callable({
        districtId,
        points,
        ...(surveyKey && surveyKey !== 'default' ? { surveyKey } : {}),
      });
      if (res.data.ok) {
        onJobStarted(res.data.jobId);
        toast('success', `一括解析を開始しました (${points.length}件)。`);
      } else {
        toast('error', `一括解析の開始に失敗しました: ${res.data.error}`);
      }
    } catch (e) {
      toast('error', `一括解析の開始に失敗しました: ${(e as Error).message}`);
    } finally {
      setStarting(false);
    }
  }

  /**
   * 完了したジョブを路線として保存する (docs/25 §10-6、MVP案)。距離は `handleCompute` 時点で
   * サーバーが返した `totalLengthM` (手描き線由来のときのみ得られる) をそのまま使う。
   * このパネルは open=false でもアンマウントされないため、ジョブ完了までパネルを閉じても
   * `candidateResult`/`lineCoords` は保持される。
   */
  async function handleSaveRoute() {
    const name = savingRouteName?.trim();
    const totalLengthM = candidateResult?.ok ? candidateResult.totalLengthM : undefined;
    if (!name || !authState.user || savingRoute || !job || totalLengthM === undefined) return;
    setSavingRoute(true);
    try {
      const postIds = job.results.filter((r) => r.ok && r.postId).map((r) => r.postId!);
      const routePosts = posts.filter((p) => postIds.includes(p.id));
      await saveRouteReportSnapshot(
        {
          districtId,
          name,
          lineCoordinates: lineCoords ?? [],
          lengthM: totalLengthM,
          postIds,
          pointCount: routePosts.length,
          axisCounts: computeAxisCounts(routePosts, primaryAxis, evaluation),
          axisKey: axisKey ?? null,
          evaluation: computeEvaluationSummary(routePosts, evaluation),
          summary: null,
          surveyKey,
        },
        authState.user.uid,
      );
      toast('success', `「${name}」として保存しました。`);
      setSavingRouteName(null);
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSavingRoute(false);
    }
  }

  if (!open) return null;

  if (!isMaster) {
    return (
      <Modal open onClose={onClose} title="一括解析" size="md">
        <p className="text-sm text-slate-600">一括解析は master 権限が必要です。</p>
      </Modal>
    );
  }

  const maxExecutable = candidateResult?.ok
    ? Math.min(MAX_EXECUTE, candidateResult.candidates.length)
    : MAX_EXECUTE;

  return (
    <Modal open onClose={handleClose} title="一括解析（道路データから候補点を自動生成）" size="xl">
      {!jobId ? (
        <>
          <div className="mb-3 flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700" htmlFor="sv-batch-interval">
              候補点の間隔:
            </label>
            <input
              id="sv-batch-interval"
              type="number"
              min={5}
              max={500}
              step={5}
              value={intervalM}
              onChange={(e) => {
                const raw = Math.round(Number(e.target.value) / 5) * 5;
                setIntervalM(Math.max(5, Math.min(500, raw || 5)));
                setCandidateResult(null);
              }}
              className="w-20 border border-slate-300 rounded px-2 py-1 text-sm"
            />
            <span className="text-xs text-slate-500">m（5m単位、既定100m）</span>
          </div>

          <Tabs
            aria-label="候補点の生成元"
            activeId={sourceMode}
            onChange={(id) => {
              setSourceMode(id as SourceMode);
              setCandidateResult(null);
            }}
            className="mb-3"
            tabs={[
              {
                id: 'layer',
                label: 'GISレイヤーから生成',
                content: (
                  <>
                    <div className="mb-4 pb-3 border-b border-slate-200">
                      <label className="text-sm font-semibold text-slate-700 block mb-1">
                        道路データを追加（.gpkg / .kml / .kmz / .geojson）
                      </label>
                      <p className="text-xs text-slate-500 mb-2">
                        交差点で分割済みの道路中心線データを推奨します（例:
                        国土数値情報の道路データ）。
                        取り込んだレイヤーは下のプルダウンで自動選択されます。
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={GIS_ACCEPT}
                          onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                          disabled={uploading}
                          className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:text-primary-700 hover:file:bg-primary-100"
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!uploadFile || uploading}
                          onClick={() => void handleUpload()}
                        >
                          {uploading ? 'アップロード中…' : 'アップロード'}
                        </Button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="text-sm font-semibold text-slate-700 block mb-1">
                        道路線レイヤーを選択
                      </label>
                      <select
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                        value={layerId}
                        onChange={(e) => {
                          setLayerId(e.target.value);
                          setCandidateResult(null);
                        }}
                      >
                        <option value="">選択してください</option>
                        {lineLayers.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name}（{l.featureCount}件）
                          </option>
                        ))}
                      </select>
                      {layersState.status === 'ready' && lineLayers.length === 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          この地区には道路線 (LineString) の GIS
                          レイヤーがありません。上のアップロード欄から追加してください。
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end mb-3">
                      <Button
                        size="sm"
                        disabled={!layerId || computing}
                        onClick={() => void handleCompute()}
                      >
                        {computing ? '計算中…' : '候補点を計算'}
                      </Button>
                    </div>
                  </>
                ),
              },
              {
                id: 'line',
                label: '地図で線を描く',
                content: lineCoords ? (
                  <>
                    <p className="text-sm text-slate-700 mb-2">
                      描いた線: 頂点 {lineCoords.length} 点
                    </p>
                    <div className="flex justify-end mb-3">
                      <Button size="sm" disabled={computing} onClick={() => void handleCompute()}>
                        {computing ? '計算中…' : '候補点を計算'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 mb-3">
                    先に地図で線を描いてください。このパネルを閉じて、地図上の「地図で線を描く」ボタンから描画してください。
                  </p>
                ),
              },
            ]}
          />

          {candidateResult &&
            (candidateResult.ok ? (
              <Card padding="md" className="mb-3 bg-slate-50">
                <p className="text-sm text-slate-700">
                  交差点＋約{intervalM}m間隔の候補点 {candidateResult.totalBeforeStreetViewFilter}{' '}
                  件のうち、 Street View が存在する地点は{' '}
                  <strong>{candidateResult.candidates.length} 件</strong>です。
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  （サーバー実測値: 間隔 {candidateResult.intervalM}m / 近接フィルタ{' '}
                  {candidateResult.minDistanceM}m）
                </p>
                {candidateResult.truncated && (
                  <p className="text-xs text-amber-700 mt-1">
                    ⚠ 候補点が多いため一部を間引いて計算しています（上限800件）。
                  </p>
                )}
                {candidateResult.candidates.length > 0 && (
                  <>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-slate-600">実行件数上限:</label>
                      <input
                        type="number"
                        min={1}
                        max={maxExecutable}
                        value={executeCount}
                        onChange={(e) =>
                          setExecuteCount(
                            Math.max(1, Math.min(maxExecutable, Number(e.target.value))),
                          )
                        }
                        className="w-20 border border-slate-300 rounded px-2 py-1 text-sm"
                      />
                      <span className="text-xs text-slate-500">
                        （最大 {maxExecutable} 件、AI の呼出 約 {executeCount * callsPerPoint}{' '}
                        回・課金が発生します）
                      </span>
                    </div>
                    <ul className="mt-2 max-h-40 overflow-auto text-xs text-slate-600 grid grid-cols-1 gap-0.5">
                      {candidateResult.candidates.slice(0, executeCount).map((c) => (
                        <li key={`${c.lat}-${c.lng}`}>
                          {c.isIntersection ? '🚦交差点' : '・補間点'} {c.lat.toFixed(6)},{' '}
                          {c.lng.toFixed(6)}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-end mt-3">
                      <Button
                        size="sm"
                        disabled={starting || executeCount < 1}
                        onClick={() => void handleExecute()}
                      >
                        {starting ? '開始中…' : `この${executeCount}件を一括解析実行`}
                      </Button>
                    </div>
                  </>
                )}
              </Card>
            ) : (
              <p className="text-sm text-danger-700">{candidateResult.error}</p>
            ))}
        </>
      ) : (
        <div>
          <p className="text-sm text-slate-700 mb-2">
            進捗: {job?.progress ?? 0} / {job?.points.length ?? '?'}
            {job?.status === 'done' && ' — 完了しました'}
            {job?.status === 'failed' && ' — 中断しました'}
          </p>
          <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
            <div
              className={`h-2 rounded-full transition-all ${
                job?.status === 'failed' ? 'bg-danger-600' : 'bg-primary-500'
              }`}
              style={{
                width: `${job && job.points.length > 0 ? (job.progress / job.points.length) * 100 : 0}%`,
              }}
            />
          </div>
          {job?.status === 'failed' && (
            <p className="text-sm text-danger-700 mb-3">
              {job.error ?? '一括解析の実行中にエラーが発生しました。'}
              {job.progress > 0 && ` ${job.progress}件目までの結果は保存されています。`}
            </p>
          )}
          {/* 路線調査の保存 (docs/25 §10-6)。「地図で線を描く」由来 (totalLengthM が得られる場合)
              のみ提供する — GISレイヤー由来の一括解析には路線の距離という概念が無いため対象外。 */}
          {job?.status === 'done' &&
            candidateResult?.ok &&
            candidateResult.totalLengthM !== undefined && (
              <div className="mb-3 pb-3 border-b border-slate-200">
                {savingRouteName === null ? (
                  <Button size="sm" variant="secondary" onClick={() => setSavingRouteName('')}>
                    路線として保存
                  </Button>
                ) : (
                  <div className="flex items-end gap-2 bg-slate-50 rounded px-3 py-2">
                    <TextField
                      label="路線名"
                      value={savingRouteName}
                      onChange={setSavingRouteName}
                      placeholder="例: 赤門〜町内会館"
                      hint={`距離: 約${Math.round(candidateResult.totalLengthM)}m`}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      disabled={!savingRouteName.trim() || savingRoute}
                      onClick={() => void handleSaveRoute()}
                    >
                      {savingRoute ? '保存中…' : '保存する'}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={savingRoute}
                      onClick={() => setSavingRouteName(null)}
                    >
                      キャンセル
                    </Button>
                  </div>
                )}
              </div>
            )}
          <ul className="max-h-60 overflow-auto grid grid-cols-1 gap-1 text-xs">
            {(job?.results ?? []).map((r, i) => (
              <li
                key={i}
                className={`px-2 py-1 rounded ${
                  r.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                }`}
              >
                {r.lat.toFixed(6)}, {r.lng.toFixed(6)} —{' '}
                {r.ok ? (r.deduped ? '既存地点あり (統合)' : '解析完了') : `失敗: ${r.error}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
